import * as THREE from 'three'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'

import { Location } from '../../core/Location'
import { Entity } from './Entity'

const LMB = 1
const RMB = 2

const FORWARD = new THREE.Vector3(0, 0, -1)

const MOVE_SPEED = 10
const MOVE_SPEED_FAST = 100
const LOOK_SPEED = 0.005
const LOOK_SLERP_SPEED = 20

const v1 = new THREE.Vector3()
const q1 = new THREE.Quaternion()
const loc1 = new Location()
const loc2 = new Location()

const PACKET_RATE = 1 / 10

export class EditorLocal extends Entity {
  constructor(client, data) {
    super(client, data)
    this.id = data.id

    this.root = new THREE.Object3D()
    this.root.position.fromArray(data.position)
    this.camera = this.client.graphics.camera
    this.root.add(this.camera)
    this.client.setHot(this, true)

    this.pointer = {
      shouldLock: false,
      locked: false,
    }
    this.mouseLeft = this.createButton()
    this.mouseRight = this.createButton()
    this.keys = {}
    this.move = new THREE.Vector3()
    this.look = new THREE.Euler(0, 0, 0, 'YXZ')

    this.setViewport(this.client.graphics.viewport)
    this.client.graphics.on('viewport', this.setViewport)

    const lineMaterial = new LineMaterial({
      color: 0x000000,
      linewidth: 2, // in pixels
      transparent: true,
      opacity: 0.5,
      // add some depth bias to prevent z-fighting
      polygonOffset: true,
      polygonOffsetFactor: -4, // more negative = more bias toward camera
      polygonOffsetUnits: -4,
    })
    // lineMaterial.resolution.set(window.innerWidth, window.innerHeight)
    this.hitbox = new LineSegments2(new THREE.BoxGeometry(), lineMaterial)
    this.hitbox.visible = false
    this.client.graphics.scene.add(this.hitbox)

    this.lastPacketTime = 0
    this.packet = {
      id: this.id,
      p: [0, 0, 0],
      q: [0, 0, 0, 1],
    }
  }

  setViewport = viewport => {
    if (this.viewport) {
      this.viewport.removeEventListener('pointerdown', this.onPointerDown)
      this.viewport.removeEventListener('pointermove', this.onPointerMove)
      this.viewport.removeEventListener('pointerup', this.onPointerUp)
      document.removeEventListener('pointerlockchange', this.onPointerLockChange)
      this.viewport.removeEventListener('contextmenu', this.onContextMenu)
      window.removeEventListener('keydown', this.onKeyDown)
      window.removeEventListener('keyup', this.onKeyUp)
    }
    this.viewport = viewport
    if (this.viewport) {
      this.viewport.addEventListener('pointerdown', this.onPointerDown)
      this.viewport.addEventListener('pointermove', this.onPointerMove)
      this.viewport.addEventListener('pointerup', this.onPointerUp)
      document.addEventListener('pointerlockchange', this.onPointerLockChange)
      this.viewport.addEventListener('contextmenu', this.onContextMenu)
      window.addEventListener('keydown', this.onKeyDown)
      window.addEventListener('keyup', this.onKeyUp)
    }
  }

  setBlockIdx(idx, sync) {
    if (this.data.blockIdx === idx) return
    this.data.blockIdx = idx
    this.emit('blockIdx', idx)
    if (sync) {
      this.client.network.send('setEditorBlockIdx', idx)
    }
  }

  setBlockAtIdx(idx, blockId, sync) {
    if (this.data.blocks[idx] === blockId) return
    this.data.blocks[idx] = blockId
    this.emit('blocks', this.data.blocks)
    if (sync) {
      this.client.network.send('setEditorBlockAtIdx', { idx, blockId })
    }
  }

  getVoiceTransform(pos, qua) {
    pos.copy(this.root.position)
    qua.copy(this.root.quaternion)
  }

  setVoiceActive(active) {
    // ...
  }

  createButton() {
    return {
      down: false,
      pressed: false,
      released: false,
    }
  }

  update(delta) {
    this.move.set(0, 0, 0)
    if (this.keys.KeyW) this.move.z -= 1
    if (this.keys.KeyS) this.move.z += 1
    if (this.keys.KeyA) this.move.x -= 1
    if (this.keys.KeyD) this.move.x += 1
    this.move.applyQuaternion(this.root.quaternion)
    if (this.keys.Space) this.move.y += 1
    if (this.keys.ControlLeft) this.move.y -= 1
    const moveSpeed = this.keys.ShiftLeft ? MOVE_SPEED_FAST : MOVE_SPEED
    this.move.multiplyScalar(moveSpeed * delta)
    this.root.position.add(this.move)
    this.root.quaternion.slerp(q1.setFromEuler(this.look), LOOK_SLERP_SPEED * delta)
    // this.engine.camera.quaternion.setFromEuler(this.look)

    if (this.keys.Digit1) this.setBlockIdx(0, true)
    if (this.keys.Digit2) this.setBlockIdx(1, true)
    if (this.keys.Digit3) this.setBlockIdx(2, true)
    if (this.keys.Digit4) this.setBlockIdx(3, true)
    if (this.keys.Digit5) this.setBlockIdx(4, true)
    if (this.keys.Digit6) this.setBlockIdx(5, true)

    // raycast
    const origin = this.root.position
    const direction = v1.copy(FORWARD).applyQuaternion(this.root.quaternion)
    const hit = this.client.world.raycast(origin, direction, 100)
    if (hit) {
      const geo = hit.block.hitbox.lineGeometry
      if (this.hitbox.geometry !== geo) {
        this.hitbox.geometry?.dispose()
        this.hitbox.geometry = geo
      }
      this.hitbox.position.set(hit.voxel.x, hit.voxel.y, hit.voxel.z)
      this.hitbox.visible = true
    } else {
      this.hitbox.visible = false
    }
    // break block
    if (hit && this.pointer.locked && this.mouseLeft.pressed) {
      loc1.voxel.copy(hit.voxel)
      this.client.chunks.editorBreakBlockAt(loc1)
    }
    // place block
    if (hit && this.pointer.locked && this.mouseRight.pressed) {
      const blockId = this.data.blocks[this.data.blockIdx]
      this.client.chunks.editorPlaceBlock(hit, this.root, blockId)
    }

    // send packets
    this.lastPacketTime += delta
    if (this.lastPacketTime > PACKET_RATE) {
      this.lastPacketTime -= PACKET_RATE
      this.root.position.toArray(this.packet.p)
      this.root.quaternion.toArray(this.packet.q)
      this.client.network.send('entityModified', this.packet)
    }
  }

  postUpdate() {
    this.mouseLeft.pressed = false
    this.mouseLeft.released = false
    this.mouseRight.pressed = false
    this.mouseRight.released = false
  }

  releaseAll() {
    this.keys = {}
  }

  onPointerDown = e => {
    this.onPointerChange(e)
    const left = !!(e.buttons & LMB)
    if (left) {
      this.lockPointer()
    }
  }

  onPointerMove = e => {
    if (!this.pointer.locked) return
    const LOOK_SPEED = 0.005
    this.look.y -= e.movementX * LOOK_SPEED
    this.look.x -= e.movementY * LOOK_SPEED
  }

  onPointerUp = e => {
    this.onPointerChange(e)
    const left = !!(e.buttons & LMB)
    if (left) {
      // ...
    }
  }

  onPointerChange = e => {
    const lmb = !!(e.buttons & LMB)
    if (!this.mouseLeft.down && lmb && this.pointer.locked) {
      this.mouseLeft.down = true
      this.mouseLeft.pressed = true
    }
    if (this.mouseLeft.down && !lmb) {
      this.mouseLeft.down = false
      this.mouseLeft.released = true
    }
    const rmb = !!(e.buttons & RMB && this.pointer.locked)
    if (!this.mouseRight.down && rmb) {
      this.mouseRight.down = true
      this.mouseRight.pressed = true
    }
    if (this.mouseRight.down && !rmb) {
      this.mouseRight.down = false
      this.mouseRight.released = true
    }
  }

  onContextMenu = e => {
    e.preventDefault()
  }

  async lockPointer() {
    this.pointer.shouldLock = true
    try {
      await this.viewport.requestPointerLock()
      return true
    } catch (err) {
      console.error(err)
      console.log('pointerlock denied, too quick?')
      return false
    }
  }

  unlockPointer() {
    this.pointer.shouldLock = false
    if (!this.pointer.locked) return
    document.exitPointerLock()
    this.onPointerLockEnd()
  }

  onPointerLockChange = e => {
    const didPointerLock = !!document.pointerLockElement
    if (didPointerLock) {
      this.onPointerLockStart()
    } else {
      this.onPointerLockEnd()
    }
  }

  onPointerLockStart() {
    if (this.pointer.locked) return
    this.pointer.locked = true
    this.emit('pointerlock', true)
    // pointerlock is async so if its no longer meant to be locked, exit
    if (!this.pointer.shouldLock) this.unlockPointer()
  }

  onPointerLockEnd() {
    if (!this.pointer.locked) return
    this.pointer.locked = false
    this.emit('pointerlock', false)
  }

  onKeyDown = e => {
    if (!this.pointer.locked) return
    this.keys[e.code] = true
  }

  onKeyUp = e => {
    // if (!this.pointer.locked) return
    const code = e.code
    if (code === 'MetaLeft' || code === 'MetaRight') {
      // releasing a meta key while another key is down causes browsers not to ever
      // trigger onKeyUp, so we just have to force all keys up
      return this.releaseAll()
    }
    this.keys[e.code] = false
  }

  destroy() {
    this.viewport.removeEventListener('pointerdown', this.onPointerDown)
    this.viewport.removeEventListener('pointermove', this.onPointerMove)
    this.viewport.removeEventListener('pointerup', this.onPointerUp)
    document.removeEventListener('pointerlockchange', this.onPointerLockChange)
    this.viewport.removeEventListener('contextmenu', this.onContextMenu)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
  }
}
